import { Component, type ReactNode, useEffect } from 'react';
import { Button, Center, Group, Stack, Text, Title } from '@mantine/core';
import { type ErrorComponentProps, useRouter, useRouterState } from '@tanstack/react-router';
import { isChunkLoadError, logger, reloadPage } from '~portal/lib';
import { PageLoading } from '~ui';

/** @desc The fallback's words: a chunk that would not load reads as an update, anything else as an error. */
const describe = (error: unknown) =>
  isChunkLoadError(error)
    ? 'This part of the portal could not be loaded. It may have been updated since you opened it — reloading fetches the latest version.'
    : 'Something went wrong while showing this part of the portal.';

/**
 * @desc The default fallback for a lazy island: one line saying what happened and a Reload
 * button. Inline-sized, so it sits where the island would have been.
 */
export function LazyFallback({ error }: { error: unknown }) {
  return (
    <Stack gap="xs" align="flex-start" role="alert" data-testid="lazy-fallback">
      <Text size="sm" c="dimmed">
        {describe(error)}
      </Text>
      <Button size="xs" variant="light" onClick={reloadPage}>
        Reload
      </Button>
    </Stack>
  );
}

type LazyBoundaryProps = {
  children: ReactNode;
  /** @desc Names the island in the log line, e.g. `MdTableGrid`. */
  name: string;
  /**
   * @desc What to render instead once the island has failed. Defaults to {@link LazyFallback}.
   * An island with a lesser version of itself (the markdown table under the grid) renders that.
   */
  fallback?: (error: unknown) => ReactNode;
};

type LazyBoundaryState = { error: unknown; failed: boolean };

/**
 * @desc The error boundary for a lazy island (`retryableLazy` under `Suspense`): a chunk that
 * still fails after its retry, or an island that throws, is kept to its own box with a Reload
 * affordance instead of taking the page down. There is no "try again": `React.lazy` remembers a
 * rejected import for the life of the page, so only a reload can fetch it afresh. Route chunks
 * do not need this — the router's `defaultErrorComponent` ({@link RouteErrorFallback}) is their
 * boundary.
 */
export class LazyBoundary extends Component<LazyBoundaryProps, LazyBoundaryState> {
  override state: LazyBoundaryState = { error: undefined, failed: false };

  static getDerivedStateFromError(error: unknown): LazyBoundaryState {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown) {
    const context = {
      island: this.props.name,
      chunk: isChunkLoadError(error),
      error: error instanceof Error ? error.message : String(error),
    };
    // An island with its own fallback still works, lesser (the plain table under the grid):
    // degraded, so `warn`. Without one the feature is gone from the page: `error`.
    if (this.props.fallback) {
      logger.warn('[lazy] Island failed; showing its fallback', context);
    } else {
      logger.error('[lazy] Island failed', context);
    }
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return this.props.fallback ? (
      this.props.fallback(this.state.error)
    ) : (
      <LazyFallback error={this.state.error} />
    );
  }
}

/**
 * @desc The router's `defaultErrorComponent`: what a route shows when its chunk will not load
 * (TanStack's `lazyRouteComponent` has already reloaded once for a missing module) or its page
 * throws. Reload is the remedy for a stale deployment; Try again re-runs the route in place, which
 * re-requests its chunk, for the blip.
 */
export function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  // `reset` clears the boundary; `invalidate` re-runs the route's loaders, which a loader-time
  // failure (a fixture chunk) needs as well — together they retry whichever part failed.
  const retry = () => {
    reset();
    void router.invalidate();
  };

  useEffect(() => {
    logger.error('[route] Route failed to render', {
      chunk: isChunkLoadError(error),
      error: error instanceof Error ? error.message : String(error),
      path: window.location.pathname,
    });
  }, [error]);

  return (
    <Center p="xl" mih={240} data-testid="route-error">
      <Stack align="center" gap="sm" maw={460} ta="center" role="alert">
        <Title order={3}>This page didn&rsquo;t load</Title>
        <Text c="dimmed" size="sm">
          {describe(error)}
        </Text>
        <Group gap="sm" mt="xs">
          <Button onClick={reloadPage}>Reload</Button>
          <Button variant="subtle" onClick={retry}>
            Try again
          </Button>
        </Group>
      </Stack>
    </Center>
  );
}

/** @desc What a slow route is loading, named from the path it is going to. */
export function pendingLabel(pathname: string): string {
  if (/^\/operators\/[^/]+/.test(pathname)) return 'Loading operator…';
  if (pathname.startsWith('/operators')) return 'Loading operators…';
  if (/^\/regions\/[^/]+/.test(pathname)) return 'Loading market…';
  if (pathname.startsWith('/regions')) return 'Loading regions…';
  return 'Loading…';
}

/**
 * @desc The router's `defaultPendingComponent`: shown only when a route's chunk or loader takes
 * longer than the router's `defaultPendingMs` (1 s) — on a fast connection the previous page simply
 * stays until the next is ready. The router has already waited, so `PageLoading` shows at once:
 * the same centred loader the pages use for their own data, so a slow route and a slow query look
 * alike, and they never stack — a page's own `PageLoading` starts its 300 ms only once the route
 * has rendered.
 */
export function RoutePending() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return <PageLoading label={pendingLabel(pathname)} delayMs={0} />;
}
